import { S3Client, PutObjectCommand, DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { fetchAuthSession } from 'aws-amplify/auth';

const BUCKET_NAME = process.env.REACT_APP_S3_PROFILE_PHOTOS_BUCKET || 'asl-ceu-tracker-profile-photos';

// Function to get S3 client with Cognito Identity Pool credentials
const getS3Client = async () => {
  try {
    const session = await fetchAuthSession();
    
    if (!session.credentials) {
      throw new Error('No valid credentials found. User may not be authenticated.');
    }

    return new S3Client({
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      credentials: session.credentials,
    });
  } catch (error) {
    console.error('Error getting S3 client:', error);
    throw error;
  }
};

export const s3PhotoService = {
  // Get photo URL (generates pre-signed URL for viewing)
  getPhotoUrl: async (userId) => {
    try {
      const s3Client = await getS3Client();
      const key = `profile-photos/${userId}/profile.jpeg`;
      
      const command = new GetObjectCommand({
        Bucket: BUCKET_NAME,
        Key: key,
      });
      
      // Generate pre-signed URL valid for 1 hour
      const url = await getSignedUrl(s3Client, command, { expiresIn: 3600 });
      return url;
    } catch (error) {
      console.error('Error getting photo URL:', error);
      return null;
    }
  },
  // Upload profile photo
  uploadProfilePhoto: async (userId, file) => {
    try {
      const s3Client = await getS3Client();
      
      // Generate unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `profile-photos/${userId}/profile.${fileExtension}`;

      // Convert file to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      const uploadParams = {
        Bucket: BUCKET_NAME,
        Key: fileName,
        Body: new Uint8Array(arrayBuffer),
        ContentType: file.type,
        CacheControl: 'max-age=31536000', // 1 year cache
        // ACL removed - bucket policy handles public access
      };

      const command = new PutObjectCommand(uploadParams);
      await s3Client.send(command);

      return {
        success: true,
        photoUrl: `https://${BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION || 'us-east-1'}.amazonaws.com/${fileName}`
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Delete profile photo
  deleteProfilePhoto: async (userId) => {
    try {
      const s3Client = await getS3Client();
      
      // Try to delete common image formats
      const extensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      for (const ext of extensions) {
        try {
          const deleteParams = {
            Bucket: BUCKET_NAME,
            Key: `profile-photos/${userId}/profile.${ext}`
          };
          
          const command = new DeleteObjectCommand(deleteParams);
          await s3Client.send(command);
        } catch (error) {
          // Continue trying other extensions
        }
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Upload CEU certificate
  uploadCertificate: async (userId, ceuId, file) => {
    try {
      const s3Client = await getS3Client();
      
      // Validate file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'jpg', 'jpeg', 'png'].includes(fileExtension)) {
        return {
          success: false,
          error: 'Invalid file type. Please upload PDF, JPG, or PNG files only.'
        };
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'File size too large. Please upload files smaller than 10MB.'
        };
      }

      const fileName = `certificates/${userId}/${ceuId}_certificate.${fileExtension}`;

      // Convert file to ArrayBuffer
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      const uploadParams = {
        Bucket: BUCKET_NAME,
        Key: fileName,
        Body: new Uint8Array(arrayBuffer),
        ContentType: file.type,
      };

      const command = new PutObjectCommand(uploadParams);
      await s3Client.send(command);

      return {
        success: true,
        certificateUrl: `https://${BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION || 'us-east-1'}.amazonaws.com/${fileName}`
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Delete certificate
  deleteCertificate: async (certificateUrl) => {
    try {
      const s3Client = await getS3Client();
      
      // Extract key from URL
      const key = new URL(certificateUrl).pathname.substring(1);
      
      const deleteParams = {
        Bucket: BUCKET_NAME,
        Key: key
      };

      const command = new DeleteObjectCommand(deleteParams);
      await s3Client.send(command);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Helper function to get profile photo URL
  getProfilePhotoUrl: function(userId, extension = 'jpg') {
    return `https://${BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION || 'us-east-1'}.amazonaws.com/${`profile-photos/${userId}/profile.${extension}`}`;
  },

  // Validate image file
  validateImageFile: (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Please select a valid image file (JPG, PNG, GIF, or WebP)'
      };
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Image must be smaller than 5MB'
      };
    }

    return { valid: true };
  }
};
